import React from "react";
import { useSelector } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";

import {
  Create,
  Edit,
  List,
  Filter,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  SelectArrayInput,
  TextField,
  FunctionField,
  required,
  CreateButton,
  EditButton,
  DeleteButton,
  FilterButton
} from "jazasoft";

import Datagrid from "../../components/Datagrid";
import HistoryButton from "../../components/LibraryHistoryButton";

import useUnit from "../../hooks/useUnit";
import { inputOptions, isEmpty, listFromCsv } from "../../utils/helpers";
import { useEntityPrivilege, useSetting } from "../../hooks";
import getValidation from "../../utils/getValidation";
import SettingKeys from "../../utils/SettingKeys";
import { DepartmentFields } from "../../utils/entityFields";
import { ResourceId, Scope } from "../../utils/privilege";
import hasPrivilege from "../../utils/hasPrivilege";

export const CreateDepartment = props => {
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRules = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);
  const { unitChoices: unitList } = useUnit();
  return (
    <Create {...props} parse={({ unitIds, ...record }) => ({ ...record, unitIds: unitIds?.join(",") })}>
      <SimpleForm defaultValue={{ unitIds: unitList?.map(e => e.id) || [] }} redirect="home">
        {unitList?.length > 1 && <SelectArrayInput validate={[required()]} source="unitIds" label="Units" choices={unitList} {...inputOptions(3)} />}
        {fields.includes(DepartmentFields.NAME) && (
          <TextInput source="name" validate={getValidation(validationRules[DepartmentFields.NAME])} {...inputOptions(3)} />
        )}
        {fields.includes(DepartmentFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRules[DepartmentFields.DESC])} {...inputOptions(3)} />
        )}
      </SimpleForm>
    </Create>
  );
};

export const EditDepartment = props => {
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRules = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);

  const record = useSelector(
    state =>
      state.jazasoft.resources[props.resource] &&
      state.jazasoft.resources[props.resource].data &&
      state.jazasoft.resources[props.resource].data[props.id]
  );
  const { unitChoices } = useUnit(record);

  return (
    <Edit
      {...props}
      format={({ unitIds, ...record }) => ({ ...record, unitIds: listFromCsv(unitIds) })}
      parse={({ unitIds, ...record }) => ({ ...record, unitIds: unitIds?.join(",") })}>
      <SimpleForm redirect="home" form="record-form-edit">
        {unitChoices?.length > 1 && (
          <SelectArrayInput validate={[required()]} source="unitIds" label="Units" choices={unitChoices} {...inputOptions(3)} />
        )}
        {fields.includes(DepartmentFields.NAME) && (
          <TextInput
            source="name"
            validate={getValidation(validationRules[DepartmentFields.NAME])}
            options={{ ...inputOptions(3), disabled: record && record.isReferenced }}
          />
        )}
        {fields.includes(DepartmentFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRules[DepartmentFields.DESC])} {...inputOptions(3)} />
        )}
        <BooleanInput source="enabled" xs={12} sm={3} />
      </SimpleForm>
    </Edit>
  );
};

const useHomeStyle = makeStyles(theme => ({
  buttonEdit: {
    width: theme.spacing(14)
  },
  buttonDelete: {
    width: theme.spacing(16)
  },
  buttonHistory: {
    width: theme.spacing(1)
  }
}));

export const DepartmentHome = props => {
  const { roles, hasAccess } = props;
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const classes = useHomeStyle();

  const { disableCreate, disableEdit, disableDelete } = useEntityPrivilege("department");
  const { allUnitList } = useUnit();
  const unitChoices = [{ id: "all", name: "All Units" }, ...allUnitList.map(({ unitId, name }) => ({ id: unitId, name }))];

  return (
    <List
      actions={({ basePath }) => (
        <div>
          {hasPrivilege(roles, hasAccess, ResourceId.DEPARTMENT, Scope.WRITE) && !disableCreate && (
            <CreateButton basePath={basePath} showLabel={false} />
          )}
          {allUnitList?.length > 1 && <FilterButton showLabel={false} />}
        </div>
      )}
      filters={
        <Filter>
          <SelectInput source="unitIds" i18nKey={props.i18nKey} choices={unitChoices} {...inputOptions(12)} />
        </Filter>
      }
      parse={({ unitIds, ...rest }) => ({ ...rest, unitIds: unitIds === "all" ? unitIds : !isEmpty(unitIds) ? `*${unitIds}*` : undefined })}
      {...props}>
      <Datagrid classes={{ headerCell: classes.headerCell, rowCell: classes.rowCell }}>
        {fields.includes(DepartmentFields.NAME) && <TextField source="name" />}
        {allUnitList.length > 1 && (
          <FunctionField
            source="unitIds"
            label="Units"
            render={record =>
              allUnitList
                .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                .map(u => u.name)
                .join(", ")
            }
          />
        )}
        {fields.includes(DepartmentFields.DESC) && <TextField source="desc" />}
        <HistoryButton cellClassName={classes.buttonHistory} i18nKey={props.i18nKey} />
        {hasPrivilege(roles, hasAccess, ResourceId.DEPARTMENT, Scope.UPDATE) && !disableEdit && <EditButton cellClassName={classes.buttonEdit} />}
        {hasPrivilege(roles, hasAccess, ResourceId.DEPARTMENT, Scope.DELETE) && !disableDelete && (
          <DeleteButton cellClassName={classes.buttonDelete} />
        )}
      </Datagrid>
    </List>
  );
};
